import {
  FACTSET_ACTIVE_GRAPH_TYPE_IDS,
  findActiveGraphCapability,
  getActiveGraphEntityConstraint,
  isKnownUnsupportedActiveGraphType,
} from "./chartCreatorRegistry";
import type {
  FactsetChartAnnotation,
  FactsetChartFormat,
  FactsetChartParameters,
  FactsetChartSummaryTable,
} from "./chartCreatorTypes";
import {
  FactsetChartFormatCoerceError,
  normalizeFactsetChartFormat,
} from "./chartFormatTheme";

export type ChartCreatorGeneratePreview = "None" | "Png" | string;

export interface ChartCreatorPayloadOptions {
  isEdit: boolean;
  generatePreview: ChartCreatorGeneratePreview;
}

export interface PreviewChartPayloadOptions {
  isEdit?: boolean;
  generatePreview?: ChartCreatorGeneratePreview;
}

export interface ChartCreatorPayload {
  data: {
    chartParameters: {
      chartType: string;
      companyNamesJson: string;
      tickers: string[];
      frequency: string;
      startDate: string;
      endDate: string;
      horizon: string;
      annotations: Array<{
        content: string;
        date: string;
        dateTime: string;
        entity: string | undefined;
        isPeerGroup: boolean;
      }>;
      stylesetId: string;
      mathFunction: string;
      quickLabel: FactsetChartParameters["quickLabel"];
      currencies: FactsetChartParameters["currencies"];
      estimateParameters: NonNullable<FactsetChartParameters["estimateParameters"]>;
    };
    chartFormat: FactsetChartFormat;
    isEdit: boolean;
    generatePreview: ChartCreatorGeneratePreview;
  };
}

export interface DownloadExcelChartPayload {
  data: Pick<ChartCreatorPayload["data"], "chartParameters" | "chartFormat"> & {
    chartParameters: ChartCreatorPayload["data"]["chartParameters"] & {
      summaryTable?: FactsetChartSummaryTable;
    };
  };
}

export const CHART_CREATOR_SUMMARY_TABLE_API = true;

export const DEFAULT_CHART_FORMAT: FactsetChartFormat = {
  fontHeight: 12,
  heading1FontHeight: 11,
  xGrid: true,
  yGrid: true,
  yAxisPosition: "Right",
  xLabelFormat: "mm/dd/yy",
};

export const DEFAULT_ESTIMATE_PARAMS: NonNullable<FactsetChartParameters["estimateParameters"]> = {
  period: "A",
  calendarized: "0",
  estimatePeriods: "3",
  actualHistory: "5",
  statistic: "mean",
  showE: "1",
  showA: "1",
};

const FREQUENCIES = new Set(["D", "W", "M", "Q", "CQ", "FQ", "LTMQ", "A", "Y", "CY", "FY"]);
const HORIZONS = new Set(["LTM", "NTM", "FY1", "FY2"]);
const DATE_TOKEN_PATTERN =
  /^(?:NOW|YTD|ALL_DATA|P_FIRST_DATE|-\d+(?:D|M|AY)|\d{1,2}\/\d{1,2}\/\d{4})$/i;
const RGB_PATTERN = /^RGB\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i;
const MAX_TICKERS = 16;
const MAX_ANNOTATIONS = 100;
const MAX_TEXT_LENGTH = 500;

export class ChartCreatorValidationError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ChartCreatorValidationError";
    this.code = code;
    Object.setPrototypeOf(this, ChartCreatorValidationError.prototype);
  }
}

function nonEmpty(value: string | undefined, fallback: string): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : fallback;
}

export function timeframeDefaultsForChartType(
  chartType: string,
): { startDate: string; frequency: string } {
  if (/^(FIN_|EST_)/i.test(chartType)) {
    return { startDate: "-5AY", frequency: "Q" };
  }
  if (/^(VAL_|SIZE_|FIX_)/i.test(chartType)) {
    return { startDate: "-3AY", frequency: "M" };
  }
  return { startDate: "-1AY", frequency: "D" };
}

function normalizeFrequency(value: string | undefined, fallback: string): string {
  return nonEmpty(value, fallback).toUpperCase();
}

function normalizeTickers(tickers: unknown): string[] {
  if (!Array.isArray(tickers)) return [];
  return Array.from(
    new Set(
      tickers
        .filter((ticker): ticker is string => typeof ticker === "string")
        .map((ticker) => ticker.trim().toUpperCase())
        .filter(Boolean),
    ),
  );
}

function normalizeAnnotations(
  annotations: readonly FactsetChartAnnotation[] | undefined,
): FactsetChartAnnotation[] | undefined {
  if (!annotations) return undefined;
  return annotations.map((annotation) => ({
    ...annotation,
    content: annotation.content.trim(),
    date: annotation.date.trim(),
    ...(annotation.dateTime ? { dateTime: annotation.dateTime.trim() } : {}),
    ...(annotation.entity ? { entity: annotation.entity.trim().toUpperCase() } : {}),
  }));
}

function canonicalChartFormat(
  format: FactsetChartFormat | undefined,
): FactsetChartFormat | undefined {
  if (!format) return undefined;
  try {
    return normalizeFactsetChartFormat(format);
  } catch (error) {
    if (error instanceof FactsetChartFormatCoerceError) {
      throw new ChartCreatorValidationError(error.code, error.message);
    }
    throw error;
  }
}

export function normalizeActiveGraphChartParameters(
  params: FactsetChartParameters,
): FactsetChartParameters {
  const chartType = typeof params.chartType === "string"
    ? params.chartType.trim().toUpperCase()
    : "";
  const timeframe = timeframeDefaultsForChartType(chartType);
  return {
    ...params,
    chartType,
    tickers: normalizeTickers(params.tickers),
    frequency: normalizeFrequency(params.frequency, timeframe.frequency),
    startDate: nonEmpty(params.startDate, timeframe.startDate).toUpperCase(),
    endDate: nonEmpty(params.endDate, "NOW").toUpperCase(),
    horizon:
      typeof params.horizon === "string" && params.horizon.trim()
        ? params.horizon.trim().toUpperCase()
        : undefined,
    companyNames: params.companyNames
      ? {
          single: params.companyNames.single?.map((name) => name.trim()).filter(Boolean),
          aggregated: params.companyNames.aggregated?.map((name) => name.trim()).filter(Boolean),
        }
      : undefined,
    annotations: normalizeAnnotations(params.annotations),
    stylesetId: params.stylesetId?.trim(),
    mathFunction: params.mathFunction?.trim(),
    chartFormat: canonicalChartFormat(params.chartFormat),
  };
}

function validateDateToken(value: string | undefined, field: string): void {
  if (!value) return;
  const absoluteDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (absoluteDate) {
    const month = Number(absoluteDate[1]);
    const day = Number(absoluteDate[2]);
    const year = Number(absoluteDate[3]);
    const parsed = new Date(0);
    parsed.setUTCHours(0, 0, 0, 0);
    parsed.setUTCFullYear(year, month - 1, day);
    if (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    ) {
      return;
    }
  } else if (DATE_TOKEN_PATTERN.test(value)) {
    return;
  }
  throw new ChartCreatorValidationError(
    "active_graph_invalid_date",
    `${field} must be NOW, YTD, ALL_DATA, P_FIRST_DATE, a relative token such as -3AY/-6M, or MM/DD/YYYY.`,
  );
}

function validateRgbMap(
  map: Record<string, string | undefined> | undefined,
  field: string,
): void {
  if (!map) return;
  for (const [key, value] of Object.entries(map)) {
    const match = value === undefined ? null : RGB_PATTERN.exec(value);
    const channels = match && value
      ? value
          .slice(value.indexOf("(") + 1, value.lastIndexOf(")"))
          .split(",")
          .map((channel) => Number(channel.trim()))
      : [];
    if (
      value !== undefined &&
      (!match ||
        channels.length !== 3 ||
        channels.some(
          (channel) =>
            !Number.isInteger(channel) || channel < 0 || channel > 255,
        ))
    ) {
      throw new ChartCreatorValidationError(
        "active_graph_invalid_format",
        `${field}.${key} must use RGB(r,g,b) with channels from 0 to 255.`,
      );
    }
  }
}

function validateChartFormat(format: FactsetChartFormat | undefined): void {
  if (!format) return;
  validateRgbMap(format.axisColor, "chartFormat.axisColor");
  validateRgbMap(format.fontColor, "chartFormat.fontColor");
  validateRgbMap(format.seriesColor, "chartFormat.seriesColor");
  for (const [field, value] of [
    ["fontHeight", format.fontHeight],
    ["heading1FontHeight", format.heading1FontHeight],
  ] as const) {
    if (value !== undefined && (!Number.isFinite(value) || value <= 0 || value > 72)) {
      throw new ChartCreatorValidationError(
        "active_graph_invalid_format",
        `chartFormat.${field} must be between 0 and 72.`,
      );
    }
  }
}

export function assertChartParametersValid(
  input: FactsetChartParameters,
  callerLabel: string,
  allowedTypeIds?: readonly string[],
  enforceCardinality = false,
): asserts input is FactsetChartParameters & { chartType: string; tickers: string[] } {
  const params = normalizeActiveGraphChartParameters(input);
  const capability = findActiveGraphCapability(params.chartType);
  if (!capability) {
    const unsupported = isKnownUnsupportedActiveGraphType(params.chartType ?? "");
    throw new ChartCreatorValidationError(
      "active_graph_chart_type_invalid",
      `[chart-capability] ${callerLabel}: chart type "${params.chartType ?? ""}" ${
        unsupported
          ? "is not supported today."
          : "is not in the 27-type ActiveGraph catalog."
      }`,
    );
  }
  if (
    allowedTypeIds &&
    !new Set(allowedTypeIds.map((id) => id.trim().toUpperCase())).has(capability.id)
  ) {
    throw new ChartCreatorValidationError(
      "active_graph_chart_type_disabled",
      `${capability.id} is not enabled for PowerPoint ActiveGraphs in this rollout.`,
    );
  }
  if (params.tickers.length === 0) {
    throw new ChartCreatorValidationError(
      "active_graph_identifiers_required",
      `${callerLabel}: at least one verified FactSet identifier is required.`,
    );
  }
  if (params.tickers.length > MAX_TICKERS) {
    throw new ChartCreatorValidationError(
      "active_graph_cardinality",
      `${callerLabel}: at most ${MAX_TICKERS} identifiers are accepted.`,
    );
  }
  if (enforceCardinality) {
    const constraint = getActiveGraphEntityConstraint(capability.id);
    if (
      constraint &&
      (params.tickers.length < constraint.minTickers ||
        (constraint.maxTickers !== undefined && params.tickers.length > constraint.maxTickers))
    ) {
      throw new ChartCreatorValidationError(
        "active_graph_cardinality",
        `${capability.id} supports ${constraint.label}; received ${params.tickers.length}.`,
      );
    }
  }
  const frequency = String(params.frequency ?? "").toUpperCase();
  if (!FREQUENCIES.has(frequency)) {
    throw new ChartCreatorValidationError(
      "active_graph_invalid_frequency",
      `${callerLabel}: unsupported frequency "${params.frequency}".`,
    );
  }
  if (params.horizon && !HORIZONS.has(String(params.horizon).toUpperCase())) {
    throw new ChartCreatorValidationError(
      "active_graph_invalid_horizon",
      `${callerLabel}: unsupported horizon "${params.horizon}".`,
    );
  }
  validateDateToken(params.startDate, "startDate");
  validateDateToken(params.endDate, "endDate");
  if (
    params.companyNames?.single &&
    params.companyNames.single.length > 0 &&
    params.companyNames.single.length !== params.tickers.length
  ) {
    throw new ChartCreatorValidationError(
      "active_graph_label_mismatch",
      `${callerLabel}: companyNames.single must contain one label per identifier.`,
    );
  }
  if ((params.annotations?.length ?? 0) > MAX_ANNOTATIONS) {
    throw new ChartCreatorValidationError(
      "active_graph_annotations_invalid",
      `${callerLabel}: at most ${MAX_ANNOTATIONS} annotations are accepted.`,
    );
  }
  for (const annotation of params.annotations ?? []) {
    if (!annotation.content || annotation.content.length > MAX_TEXT_LENGTH || !annotation.date) {
      throw new ChartCreatorValidationError(
        "active_graph_annotations_invalid",
        `${callerLabel}: annotations require bounded content and a date.`,
      );
    }
    if (annotation.entity && !params.tickers.includes(annotation.entity)) {
      throw new ChartCreatorValidationError(
        "active_graph_annotations_invalid",
        `${callerLabel}: annotation entity "${annotation.entity}" is not one of the chart identifiers.`,
      );
    }
  }
  validateChartFormat(params.chartFormat);
}

function annotationDateTime(date: string, dateTime: string | undefined): string {
  const explicit = typeof dateTime === "string" ? dateTime.trim() : "";
  if (explicit.length > 0) return explicit;
  const trimmed = date.trim();
  const slashDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!slashDate && !isoDate) return trimmed;
  const year = Number(slashDate?.[3] ?? isoDate?.[1]);
  const month = Number(slashDate?.[1] ?? isoDate?.[2]);
  const day = Number(slashDate?.[2] ?? isoDate?.[3]);
  const parsedDate = new Date(0);
  parsedDate.setUTCHours(0, 0, 0, 0);
  parsedDate.setUTCFullYear(year, month - 1, day);
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return trimmed;
  }
  return parsedDate.toISOString();
}

export function buildChartCreatorPayload(
  input: FactsetChartParameters,
  options: ChartCreatorPayloadOptions,
): ChartCreatorPayload {
  const params = normalizeActiveGraphChartParameters(input);
  assertChartParametersValid(params, "previewChart");
  const companyNames = params.companyNames ?? { single: [], aggregated: [] };
  return {
    data: {
      chartParameters: {
        chartType: params.chartType!,
        companyNamesJson: JSON.stringify({
          SingleCompanies: companyNames.single ?? [],
          AggregatedCompanies: companyNames.aggregated ?? [],
        }),
        tickers: params.tickers,
        frequency: params.frequency!,
        startDate: params.startDate!,
        endDate: params.endDate!,
        horizon: params.horizon ?? "",
        annotations: (params.annotations ?? []).map((annotation) => ({
          content: annotation.content,
          date: annotation.date,
          dateTime: annotationDateTime(annotation.date, annotation.dateTime),
          entity: annotation.entity ?? params.tickers[0],
          isPeerGroup: annotation.isPeerGroup ?? false,
        })),
        stylesetId: params.stylesetId ?? "",
        mathFunction: params.mathFunction ?? "",
        quickLabel: params.quickLabel ?? 0,
        currencies: params.currencies ?? [],
        estimateParameters: params.estimateParameters ?? DEFAULT_ESTIMATE_PARAMS,
      },
      chartFormat: { ...DEFAULT_CHART_FORMAT, ...(params.chartFormat ?? {}) },
      isEdit: options.isEdit,
      generatePreview: options.generatePreview,
    },
  };
}

export function buildPreviewChartPayload(
  params: FactsetChartParameters,
  options: PreviewChartPayloadOptions = {},
): ChartCreatorPayload {
  return buildChartCreatorPayload(params, {
    isEdit: options.isEdit ?? false,
    generatePreview: options.generatePreview ?? "Png",
  });
}

export function buildDownloadExcelChartPayload(
  params: FactsetChartParameters,
  options: { summaryTableApiEnabled?: boolean } = {},
): DownloadExcelChartPayload {
  const shared = buildChartCreatorPayload(params, {
    isEdit: false,
    generatePreview: "None",
  });
  const summaryTableEnabled =
    options.summaryTableApiEnabled ?? CHART_CREATOR_SUMMARY_TABLE_API;
  return {
    data: {
      chartParameters: {
        ...shared.data.chartParameters,
        ...(summaryTableEnabled && params.summaryTable
          ? { summaryTable: params.summaryTable }
          : {}),
      },
      chartFormat: shared.data.chartFormat,
    },
  };
}

export function buildFactsetChartParametersSchema(
  allowedTypeIds: readonly string[] = FACTSET_ACTIVE_GRAPH_TYPE_IDS,
): Record<string, unknown> {
  return {
    type: "object",
    additionalProperties: false,
    required: ["chartType", "tickers"],
    properties: {
      chartType: {
        type: "string",
        enum: [...allowedTypeIds],
        description:
          "Enabled FactSet Chart Creator type. VAL_PE2 is trailing/LTM P/E only.",
      },
      tickers: {
        type: "array",
        minItems: 1,
        maxItems: MAX_TICKERS,
        items: { type: "string", minLength: 1 },
        description: "Verified FactSet identifiers such as AAPL-US.",
      },
      companyNames: {
        type: "object",
        additionalProperties: false,
        properties: {
          single: { type: "array", items: { type: "string" } },
          aggregated: { type: "array", items: { type: "string" } },
        },
      },
      frequency: {
        type: "string",
        enum: [...FREQUENCIES],
      },
      startDate: { type: "string", minLength: 1 },
      endDate: { type: "string", minLength: 1 },
      horizon: { type: "string", enum: [...HORIZONS] },
      annotations: {
        type: "array",
        maxItems: MAX_ANNOTATIONS,
        description:
          "Optional chart annotations. Resolve content/date/entity explicitly; do not invent annotations.",
        items: {
          type: "object",
          additionalProperties: false,
          required: ["content", "date"],
          properties: {
            content: {
              type: "string",
              minLength: 1,
              maxLength: MAX_TEXT_LENGTH,
            },
            date: { type: "string", minLength: 1 },
            dateTime: { type: "string" },
            entity: { type: "string" },
            isPeerGroup: { type: "boolean" },
          },
        },
      },
      stylesetId: {
        type: "string",
        description:
          "Optional FactSet skin id returned by list_active_graph_stylesets. Never guess an id. " +
          "A styleset is not a bank brand or the open-deck theme; use chartFormat.seriesColor for those.",
      },
      mathFunction: {
        type: "string",
        description:
          "Optional chart math function such as relative_pct. Emit only when the user asks and the token is known; never guess.",
      },
      quickLabel: {
        anyOf: [
          {
            type: "string",
            enum: [
              "None",
              "Default",
              "Latest",
              "PercentChange",
              "AbsoluteChange",
              "Avg",
              "Max",
              "Min",
            ],
          },
          { type: "number" },
        ],
        description:
          "Optional endpoint/quick-label: None, Default, Latest, PercentChange, AbsoluteChange, Avg, Max, Min. " +
          "Omit unless the user asks for an endpoint/latest/change label on the series.",
      },
      currencies: {
        type: "array",
        items: {
          type: "object",
          additionalProperties: false,
          required: ["name", "iso"],
          properties: {
            name: { type: "string" },
            iso: { type: "string" },
            isEntity: { type: "boolean" },
          },
        },
      },
      estimateParameters: {
        type: "object",
        additionalProperties: false,
        properties: {
          period: { type: "string", enum: ["A", "Q"] },
          calendarized: { type: "string" },
          estimatePeriods: { anyOf: [{ type: "string" }, { type: "number" }] },
          actualHistory: { anyOf: [{ type: "string" }, { type: "number" }] },
          statistic: { type: "string" },
          showE: { type: "string" },
          showA: { type: "string" },
        },
      },
      chartFormat: {
        type: "object",
        additionalProperties: false,
        description:
          "Optional Chart Creator style. Colors use RGB(r,g,b), 0-255. " +
          "seriesColor keys are seriesColor0…seriesColor15 in tickers[] order. " +
          "fontColor/axisColor style heading/legend/axis chrome; they do not set heading text. " +
          "Omit fields to retain Chart Creator defaults.",
        properties: {
          axisColor: {
            type: "object",
            additionalProperties: { type: "string" },
            description:
              "RGB(r,g,b). Keys: xAxisFGColor, yAxisFGColor (axis lines); xFontColor, yFontColor (tick labels); " +
              "xTitleFontColor, yTitleFontColor (axis titles — emit title keys only if shown). Omit keeps FactSet/styleset chrome.",
          },
          fontColor: {
            type: "object",
            additionalProperties: { type: "string" },
            description:
              "RGB(r,g,b). heading1FontColor = main title color (not title text), heading2FontColor = subtitle, legendFontColor = legend. Omit keeps FactSet/styleset chrome.",
          },
          fontHeight: {
            type: "number",
            description: "Legend/axis/subtitle pt. Omit to keep default 12.",
          },
          heading1FontHeight: {
            type: "number",
            description: "Main title pt. Omit to keep default 11.",
          },
          seriesColor: {
            type: "object",
            additionalProperties: { type: "string" },
            description:
              "Per-series RGB(r,g,b). Keys seriesColor0…seriesColor15 matching tickers[] order. Omit for FactSet default blue.",
          },
          xGrid: {
            type: "boolean",
            description: "Category grid visibility. Omit keeps default true.",
          },
          xLabelFormat: {
            type: "string",
            description:
              "Lowercase date layout, e.g. mm/dd/yy, dd-mmm-yy. Emit only when the user asks to change date labels.",
          },
          yAxisPosition: {
            type: "string",
            enum: ["Left", "Right"],
            description: "Value-axis side. Omit keeps Right.",
          },
          yGrid: {
            type: "boolean",
            description: "Value grid visibility. Omit keeps default true.",
          },
          yLabelPrefix: {
            type: "string",
            description:
              'Value-axis prefix such as "$" or "¥"; "" clears. Set only when correct for the displayed unit/currency.',
          },
        },
      },
    },
  };
}
